import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import {
	type CollabDraftQuery$data,
	type CollabDraftQuery$variables,
} from '@confluence/fabric-providers/entry-points/CollabDraftQuery.ts';
import {
	type CollabDraftWithRelevanceQuery$data,
	type CollabDraftWithRelevanceQuery$variables,
} from '@confluence/fabric-providers/entry-points/CollabDraftWithRelevanceQuery.ts';
import {
	isCollabDraftCacheStale,
	markCollabDraftFetched,
} from '@confluence/fabric-providers/entry-points/CollabDraftCacheManager.ts';
import { getCollabDraftQuery } from '@confluence/fabric-providers/entry-points/getCollabDraftQuery.ts';

/**
 * Prefetch collabDraft content into the Apollo cache for a given content ID.
 * This is a lightweight version of preloadCollabDraftContent from load-edit-page,
 * without the edit-page-specific side effects (e.g. __SMART_CARDS_IN_DRAFT__).
 */
function prefetchCollabDraft(contentId: string, useCache: boolean) {
	return query<
		CollabDraftQuery$data | CollabDraftWithRelevanceQuery$data,
		CollabDraftQuery$variables | CollabDraftWithRelevanceQuery$variables
	>({
		query: getCollabDraftQuery(),
		variables: {
			id: contentId,
			hydrateAdf: true,
		},
		fetchPolicy: useCache ? 'cache-first' : 'network-only',
	});
}

// Tracking for prefetch timing — lets us measure if/when the cache was used
// on subsequent navigation. Keyed by contentId.
const prefetchTimestamps: Record<
	string,
	{
		startedAt: number;
		completedAt?: number;
	}
> = {};
export type PrefetchStatus = 'not_prefetched' | 'in_flight' | 'ready' | 'cache_expired';

export type PrefetchTimingInfo = {
	status: PrefetchStatus;
	prefetchDurationMs?: number;
	timeSincePrefetchMs?: number;
	headStartMs?: number;
	cacheStale: boolean;
};

/**
 * Get prefetch timing info for analytics purposes.
 */
export function getPrefetchTimingInfo(contentId: string): PrefetchTimingInfo {
	const timing = prefetchTimestamps[contentId];
	const cacheStale = isCollabDraftCacheStale(contentId);
	const now = performance.now();

	if (!timing) {
		return { status: 'not_prefetched', cacheStale };
	}

	if (!timing.completedAt) {
		const headStartMs = now - timing.startedAt;
		return { status: 'in_flight', headStartMs, cacheStale };
	}

	const timeSincePrefetchMs = now - timing.completedAt;
	const prefetchDurationMs = timing.completedAt - timing.startedAt;

	if (cacheStale) {
		return { status: 'cache_expired', prefetchDurationMs, timeSincePrefetchMs, cacheStale };
	}

	return { status: 'ready', prefetchDurationMs, timeSincePrefetchMs, cacheStale };
}

export function doPrefetch(contentId: string): void {
	const useCache = !isCollabDraftCacheStale(contentId);

	prefetchTimestamps[contentId] = { startedAt: performance.now() };

	prefetchCollabDraft(contentId, useCache)
		.then(() => {
			markCollabDraftFetched(contentId);
			prefetchTimestamps[contentId].completedAt = performance.now();
		})
		.catch(() => {
			// Swallow errors — this is a best-effort prefetch.
			// The real fetch on navigation will handle errors properly.
		});
}
